import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { formatInTimeZone } from 'date-fns-tz';
import type AbilitiesService from 'ember-can/services/abilities';
import { task } from 'ember-concurrency';
import type { Task } from 'ember-concurrency';
import link from 'ember-link/helpers/link';
import EmberTooltip from 'ember-tooltips/components/ember-tooltip';
import { and, eq, not, or } from 'ember-truth-helpers';
import MultipleScreenersInfo from 'garaje/components/approvals/multiple-screeners-info';
import NoReviewPermissionTooltip from 'garaje/components/approvals/no-review-permission-tooltip';
import ReviewButtons from 'garaje/components/approvals/screeners/review-buttons';
import UnifiedScreenersModal from 'garaje/components/approvals/unified-screeners-modal';
import WorkflowDecisionModal from 'garaje/components/approvals/workflow-decision-modal';
import LineClamp from 'garaje/components/line-clamp';
import MonogramOrPhoto from 'garaje/components/monogram-or-photo';
import UiPill from 'garaje/components/ui-pill';
import arrayToSentence from 'garaje/helpers/array-to-sentence';
import removedString from 'garaje/helpers/removed-string';
import type { InviteWithVisitApproval } from 'garaje/models/invite';
import type ScreenerResult from 'garaje/models/screener-result';
import italicizeIfRemoved from 'garaje/modifiers/italicize-if-removed';
import type AjaxService from 'garaje/services/ajax';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type StateService from 'garaje/services/state';
import type { WorkflowTasksPermissionsResponse } from 'garaje/types/workflow-approvals';
import {
  deriveCanApproveWorkflow,
  deriveUserIsAssignedApproverWithoutPermission,
} from 'garaje/utils/derive-can-approve-workflow';
import { getScreenerDisplayName } from 'garaje/utils/get-screener-display-name';
import { reads } from 'macro-decorators';

interface VisitorsApprovalsTableRowSignature {
  Args: {
    approveInviteTask: Task<void, [InviteWithVisitApproval, reason?: string]>;
    columns: string[];
    denyInviteTask: Task<void, [InviteWithVisitApproval, reason?: string]>;
    hideReviewButtons: boolean; // whether Approve/Deny buttons should _not_ be shown
    invite: InviteWithVisitApproval;
    isSelected: boolean; // whether this invite is "selected" (for bulk actions)

    selectOrDeselectInvite: (invite: InviteWithVisitApproval) => unknown;
  };
  Element: HTMLTableRowElement;
}

const DEFAULT_TIMEZONE = 'Etc/UTC'; // used if currentLocation is not present or is missing a timezone

const EXPECTED_ARRIVAL_DATE_FORMAT = 'MMM d, yyyy';
const EXPECTED_ARRIVAL_TIME_FORMAT = 'h:mm aaa';

export default class VisitorsApprovalsTableRow extends Component<VisitorsApprovalsTableRowSignature> {
  @service declare abilities: AbilitiesService;
  @service declare ajax: AjaxService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare state: StateService;

  @reads('state.features.canAccessMultiPartApprovals') canAccessMultiPartApprovals!: boolean;

  @tracked focusedResult?: ScreenerResult | null = null;
  @tracked workflowPermissionsChecked = false;
  @tracked canApproveWorkflow = true;
  @tracked userIsAssignedApproverWithoutPermission = false;
  @tracked showWorkflowDecisionModal = false;
  @tracked workflowDecisionAction: 'approve' | 'deny' | null = null;
  @tracked workflowDecisionReason?: string;

  constructor(owner: unknown, args: VisitorsApprovalsTableRowSignature['Args']) {
    super(owner, args);

    if (this.featureFlags.isEnabled('ava-feature') && this.workflowId) {
      void this.fetchWorkflowPermissionsTask.perform();
    } else {
      this.workflowPermissionsChecked = true;
    }
  }

  get workflowId(): string | null {
    if (!this.featureFlags.isEnabled('ava-feature')) {
      return null;
    }

    const resultWithWorkflow = this.args.invite.visitApproval.screenerResults.find(
      (result) => result.workflowId !== null,
    );
    return resultWithWorkflow?.workflowId ?? null;
  }

  get isPartiallyApproving(): boolean {
    return this.args.invite.visitApproval.partiallyApprovable;
  }

  get unreviewableScreenerSources(): string {
    return arrayToSentence(
      this.args.invite.visitApproval.unreviewableScreenerSources.map(
        (source) => getScreenerDisplayName(source) ?? source,
      ),
    );
  }

  get hasMultipleApprovals(): boolean {
    return this.args.invite.visitApproval.reviewResults.length > 1;
  }

  get approveCopy(): string {
    // has multiple approvals and can review all
    if (this.hasMultipleApprovals && this.abilities.can('review entry-approvals', this.#permissionsModel))
      return 'Approve all';
    return 'Approve';
  }

  get expectedArrivalDate(): string | null {
    const invite = this.args.invite;
    return invite.expectedArrivalTime
      ? formatInTimeZone(
          invite.expectedArrivalTime,
          this.state.currentLocation?.timezone ?? DEFAULT_TIMEZONE,
          EXPECTED_ARRIVAL_DATE_FORMAT,
        )
      : null;
  }

  get expectedArrivalTime(): string | null {
    const invite = this.args.invite;
    return invite.expectedArrivalTime
      ? formatInTimeZone(
          invite.expectedArrivalTime,
          this.state.currentLocation?.timezone ?? DEFAULT_TIMEZONE,
          EXPECTED_ARRIVAL_TIME_FORMAT,
        )
      : null;
  }

  get approvalResults(): ScreenerResult[] {
    return this.args.invite.visitApproval.reviewResults.filter((result) => {
      // `result.status` is only present on blocklist reports, not others.
      if (result.screenerSource === 'blacklist' && result.status !== 'review') return false;
      if (result.screenerSource === 'visual-compliance' && !result.didFail) return false;
      // do not show conditional rules in a pending state
      if (result.screenerSource === 'conditional-rule' && result.isPending) return false;
      return true;
    });
  }

  get canReviewInvite(): boolean {
    // if we are using multi part approvals, we should still be able to review or read parts of the approval that we have access to
    if (this.canAccessMultiPartApprovals) return this.canReviewSomeReviewableScreeners;
    return this.abilities.can('review entry-approvals', this.#permissionsModel);
  }

  /**
   * user can review at least one screener that is in a review state
   */
  get canReviewSomeReviewableScreeners(): boolean {
    return this.abilities.can('review some reviewable entry-approvals', this.#permissionsModel);
  }

  // Show an "Edited" pill when the invite was edited in such a way as to trigger rescreening.
  // This can be done by, for example, changing the Visitor Type. This status is communicated from
  // the backend by the `employeeTriggeredRescreen` property of the invite's `approvalStatus` or `visitApproval`.
  // For invites with an advanced approval workflow, we also show "Edited" when a visitor changes
  // data, indicated by the `visitorTriggeredRescreen` property.
  get showEditedIndication(): boolean {
    const { visitApproval } = this.args.invite;
    return (
      Boolean(visitApproval.employeeTriggeredRescreen) ||
      (Boolean(this.workflowId) && Boolean(visitApproval.visitorTriggeredRescreen))
    );
  }

  get reviewButtonsHidden(): boolean {
    return this.args.hideReviewButtons || !this.canReviewInvite;
  }

  get reviewButtonsDisabled(): boolean {
    return (
      this.reviewButtonsHidden ||
      this.approveInviteTask.isRunning ||
      this.denyInviteTask.isRunning ||
      !this.workflowPermissionsChecked ||
      !this.canApproveWorkflow
    );
  }

  get #permissionsModel() {
    return {
      context: 'location',
      parent: this.args.invite,
      visitApproval: this.args.invite.visitApproval,
    };
  }

  /**
   * Fetches workflow permissions from the API to determine if the user can approve/deny this invite.
   * If the invite has a workflowId, we need to check if the user has permission to approve it.
   */
  fetchWorkflowPermissionsTask = task(async (): Promise<void> => {
    if (!this.workflowId) {
      this.workflowPermissionsChecked = true;
      return;
    }

    try {
      const response = await this.ajax.request<WorkflowTasksPermissionsResponse>(
        `/a/visits/api/v1/workflows/${this.workflowId}/tasks/permissions`,
      );

      this.canApproveWorkflow = deriveCanApproveWorkflow(response);
      this.userIsAssignedApproverWithoutPermission = deriveUserIsAssignedApproverWithoutPermission(response);
    } catch (_error) {
      // Any error (404, 500, etc.) should disable the buttons
      this.canApproveWorkflow = false;
      this.userIsAssignedApproverWithoutPermission = false;
    } finally {
      this.workflowPermissionsChecked = true;
    }
  });

  /**
   * These component-local versions of tasks exist so we can get instance-specific state.
   * The actual logic lives at a higher level so that, when invoked with `.unlinked()`, it all
   * executes; otherwise, once the reviewed invite was reloaded (either with an `invite.reload()`
   * call or by the realtime subscription that gets notified of changes to invites and pushes them
   * into the store out-of-band), this component gets torn down and the task is aborted before,
   * e.g., the flash message is shown. By keeping that logic out of this component, we don't
   * have to worry about that.
   * The instance-local state means that we can disable this invite's Approve/Deny buttons when
   * approveInviteTask or denyInviteTask is running without having to track state ourselves at
   * the higher level, or ended up disabling the buttons for _all_ invites instead of this one.
   */

  approveInviteTask = task(async (): Promise<void> => {
    await this.args.approveInviteTask.unlinked().perform(this.args.invite);
  });

  denyInviteTask = task(async (): Promise<void> => {
    await this.args.denyInviteTask.unlinked().perform(this.args.invite);
  });

  @action
  openWorkflowDecisionModal(action: 'approve' | 'deny'): void {
    if (this.workflowId) {
      this.workflowDecisionAction = action;
      this.showWorkflowDecisionModal = true;
    }
  }

  @action
  closeWorkflowDecisionModal(): void {
    this.showWorkflowDecisionModal = false;
    this.workflowDecisionAction = null;
    this.workflowDecisionReason = undefined;
  }

  @action
  handleWorkflowDecisionConfirm(reason?: string): void {
    this.workflowDecisionReason = reason;

    if (this.workflowDecisionAction === 'approve') {
      void this.args.approveInviteTask.unlinked().perform(this.args.invite, reason);
    } else if (this.workflowDecisionAction === 'deny') {
      void this.args.denyInviteTask.unlinked().perform(this.args.invite, reason);
    }
    this.closeWorkflowDecisionModal();
  }

  @action
  handleApproveClick(): void {
    if (this.workflowId) {
      this.openWorkflowDecisionModal('approve');
    } else {
      void this.approveInviteTask.perform();
    }
  }

  @action
  handleDenyClick(): void {
    if (this.workflowId) {
      this.openWorkflowDecisionModal('deny');
    } else {
      void this.denyInviteTask.perform();
    }
  }

  // local helper
  @action
  showColumn(columnName: string): boolean {
    return this.args.columns.includes(columnName);
  }

  @action
  toggleModal(result?: ScreenerResult | null): void {
    this.focusedResult = result;
  }

  <template>
    <tr data-test-approval-required-row={{@invite.id}}>
      <td class="align-middle border-b">
        <input
          type="checkbox"
          checked={{@isSelected}}
          disabled={{not this.canReviewInvite}}
          {{on "click" (fn @selectOrDeselectInvite @invite)}}
        />
      </td>

      <td class="align-middle border-b" style="text-wrap: balance" data-test-expected-arrival-time>
        {{#if this.expectedArrivalDate}}
          {{this.expectedArrivalDate}}
          <br />
          <span class="text-carbon-50">{{this.expectedArrivalTime}}</span>
        {{/if}}
      </td>

      {{#if (this.showColumn "name")}}
        <td class="align-middle border-b pl-[10px]" data-test-visitor-name>
          <div class="flex items-center">
            <MonogramOrPhoto
              @href={{link route="visitors.invites.show" model=@invite.id}}
              @name={{removedString @invite.fullName}}
              @devicePhoto={{or @invite.photoUrl @invite.entry.content.thumbnails.small}}
              @passportPhoto={{@invite.entry.content.passportAvatar}}
              @borderBottom={{false}}
              class="pl-0"
            />
            <div class="flex flex-col pl-2">
              <span class="whitespace-nowrap" data-test-visitor-fullname>
                <LinkTo
                  @route="visitors.invites.show"
                  @model={{@invite.id}}
                  class="text-base font-bold no-underline text-carbon-70 hover:text-red-50 trans-color"
                  data-test-invite-link-to={{@invite.id}}
                  {{italicizeIfRemoved @invite.fullName}}
                >
                  {{removedString @invite.fullName}}
                </LinkTo>

                {{#if this.showEditedIndication}}
                  <UiPill @fill="newLightBlue" data-test-edited-flag>
                    Edited
                  </UiPill>
                {{/if}}
              </span>

              {{#if @invite.host}}
                <p class="text-sm text-carbon-50 m-0 mt-1">
                  <strong>Host</strong>:
                  <span data-test-host-name {{italicizeIfRemoved @invite.host}}>{{removedString @invite.host}}</span>
                </p>
              {{/if}}
            </div>
          </div>
        </td>
      {{/if}}

      {{#if (this.showColumn "entryStatus")}}
        <td class="align-middle border-b text-sm text-carbon-50" data-test-entry-status>
          <div class="h-12 flex justify-center flex-col">
            {{#if @invite.approvalWasDenied}}
              <div class="flex items-center">
                <img
                  data-test-emp-reg-status="denied"
                  src="/assets/images/cross-small-gray.svg"
                  alt=""
                  role="none"
                  class="mr-2 w-[11px] h-[10px]"
                />
                Entry denied
              </div>
            {{else if @invite.approved}}
              <div class="flex items-center">
                <img
                  data-test-emp-reg-status="approved"
                  src="/assets/images/checkmark-small-gray.svg"
                  alt=""
                  role="none"
                  class="mr-2 w-[11px] h-[10px]"
                />
                Approved
              </div>
            {{else if @invite.pending}}
              Pending
            {{else if @invite.needsPropertyApprovalReview}}
              <div class="flex items-center">
                <img src="/assets/images/watch-later.svg" alt="" role="none" class="mr-2 w-[14px] h-[14px]" />
                Pending
              </div>
              <p class="text-xs mb-0 whitespace-nowrap">Property block list triggered.</p>
              <p class="text-xs mb-0 whitespace-nowrap">You may contact your property team.</p>
            {{/if}}
          </div>
        </td>
      {{/if}}

      {{#if (this.showColumn "type")}}
        <td class="align-middle border-b text-carbon-50" data-test-visitor-type>
          {{@invite.flowName}}
        </td>
      {{/if}}

      {{#if (this.showColumn "approvalType")}}
        <td class="align-middle border-b text-carbon-50" data-test-approval-type>
          <MultipleScreenersInfo
            @model={{@invite}}
            @results={{this.approvalResults}}
            @toggleModal={{this.toggleModal}}
          />
        </td>
      {{/if}}

      {{#if (this.showColumn "returningVisitor")}}
        <td class="align-middle border-b text-center text-carbon-50" data-test-returning-visitor>
          {{#if @invite.beenHereBefore}}
            <img
              alt="{{removedString @invite.fullName}} is a returning visitor"
              src="/assets/images/checkmark-small-gray.svg"
              data-test-invite-returning-visitor
            />
          {{/if}}
        </td>
      {{/if}}

      {{#if (this.showColumn "privateNotes")}}
        <td class="align-middle border-b text-sm max-w-[18rem]" data-test-private-notes>
          {{#if @invite.privateNotes}}
            <LineClamp
              class="m-0 italic break-words"
              @text={{removedString @invite.privateNotes}}
              @lines={{2}}
              @lineHeight={{1.25}}
            />
          {{/if}}
        </td>
      {{/if}}

      <td class="align-middle whitespace-nowrap border-b">
        <button
          data-test-approve-button
          type="button"
          class="btn btn--outline btn--small mr-4 w-[8rem] {{if this.reviewButtonsHidden 'invisible'}}"
          disabled={{this.reviewButtonsDisabled}}
          {{on "click" this.handleApproveClick}}
        >
          <span class="icon-check-green mr-1"></span>
          {{this.approveCopy}}
          {{#if (and this.isPartiallyApproving (not this.reviewButtonsHidden))}}
            {{! prettier-ignore }}
            <EmberTooltip
              @tooltipClass="!rounded-none !p-2 !border !border-carbon-20 !text-carbon-80 !bg-white text-shadow-none z-50 !max-w-xs [&_.ember-tooltip-arrow]:hidden"
            >
              Approving this invite will still require additional approvals for <strong>{{this.unreviewableScreenerSources}}</strong> from another admin
            </EmberTooltip>
          {{/if}}
        </button>
        <button
          data-test-deny-button
          type="button"
          class="btn btn--outline btn--small ml-2 w-[8rem] {{if this.reviewButtonsHidden 'invisible'}}"
          disabled={{this.reviewButtonsDisabled}}
          {{on "click" this.handleDenyClick}}
        >
          <span class="icon-cross-red mr-1"></span>
          {{if this.hasMultipleApprovals "Deny all" "Deny"}}
        </button>
        {{#if this.userIsAssignedApproverWithoutPermission}}
          <NoReviewPermissionTooltip />
        {{/if}}
      </td>
    </tr>

    {{#if this.focusedResult}}
      <UnifiedScreenersModal
        @model={{@invite}}
        @focusedResult={{this.focusedResult}}
        @onClose={{fn this.toggleModal null}}
      >
        <:actions as |result onApprove|>
          {{#unless this.reviewButtonsHidden}}
            <ReviewButtons
              @onApprove={{onApprove}}
              @approveTask={{this.approveInviteTask}}
              @denyTask={{this.denyInviteTask}}
              @result={{result}}
              @model={{@invite}}
            >
              <:deny>
                Deny visit
              </:deny>
            </ReviewButtons>
          {{/unless}}
        </:actions>
      </UnifiedScreenersModal>
    {{/if}}

    {{#if this.showWorkflowDecisionModal}}
      <WorkflowDecisionModal
        @isApproving={{eq this.workflowDecisionAction "approve"}}
        @onClose={{this.closeWorkflowDecisionModal}}
        @onConfirm={{this.handleWorkflowDecisionConfirm}}
      />
    {{/if}}
  </template>
}
